import matter from "gray-matter";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import ReactMarkdown from "react-markdown";

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const response = await fetch(`/markdowns/${slug}.md`);
        if (!response.ok) {
          throw new Error("Blog post not found");
        }
        const content = await response.text();
        console.log(`Loaded blog post: ${content}`);
        const { data, content: body } = matter(content);

        setPost({
          title: data.title || "Untitled",
          date: data.date || new Date().toISOString(),
          content: body,
          ...data,
        });
      } catch (error) {
        console.error("Error loading blog post:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug]);

  const containerStyle = {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    minHeight: "100vh",
    background: "#fafafa",
    color: "#1a1a2e",
    padding: isMobile ? "2rem 1rem" : "4rem 2rem",
  };

  const articleStyle = {
    maxWidth: "800px",
    margin: "0 auto",
    fontFamily: "Inter, sans-serif",
  };

  const markdownStyles = {
    fontFamily: "Inter, sans-serif",
    lineHeight: 1.7,
    fontSize: isMobile ? "1rem" : "1.125rem",
    color: "#1a1a2e",
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center", marginTop: "4rem" }}>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "1.25rem",
              color: "rgba(26,26,46,0.6)",
            }}
          >
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center", marginTop: "4rem" }}>
          <h1
            style={{
              color: "#1a1a2e",
              fontSize: "2rem",
              marginBottom: "1rem",
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
            }}
          >
            Blog Post Not Found
          </h1>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "1.25rem",
              marginBottom: "2rem",
              color: "rgba(26,26,46,0.6)",
            }}
          >
            {error}
          </p>
          <Link
            to="/blog"
            style={{
              padding: "0.75rem 1.5rem",
              background: "#1a1a2e",
              color: "#fff",
              textDecoration: "none",
              borderRadius: "0.5rem",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
            }}
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <Link
        to="/blog"
        style={{
          position: "absolute",
          top: "2rem",
          left: "2rem",
          padding: "0.55rem 1rem",
          border: "1px solid #1a1a2e",
          borderRadius: "0.5rem",
          background: "transparent",
          color: "#1a1a2e",
          textDecoration: "none",
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          fontSize: "0.875rem",
        }}
      >
        ← Back to Blog
      </Link>

      <article style={articleStyle}>
        <header style={{ marginBottom: "3rem", textAlign: "center" }}>
          <h1
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: isMobile ? "2rem" : "3rem",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              margin: "2rem 0 1rem",
              color: "#1a1a2e",
              lineHeight: 1.2,
            }}
          >
            {post.title}
          </h1>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "1rem",
              color: "rgba(26,26,46,0.45)",
              margin: 0,
            }}
          >
            {new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </header>

        <div style={markdownStyles}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <h1
                  style={{
                    fontSize: isMobile ? "2rem" : "2.5rem",
                    color: "#1a1a2e",
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    marginTop: "3rem",
                    marginBottom: "1rem",
                    lineHeight: 1.2,
                  }}
                >
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2
                  style={{
                    fontSize: isMobile ? "1.5rem" : "2rem",
                    color: "#1a1a2e",
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    marginTop: "2.5rem",
                    marginBottom: "1rem",
                    lineHeight: 1.3,
                  }}
                >
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3
                  style={{
                    fontSize: isMobile ? "1.25rem" : "1.5rem",
                    color: "#1a1a2e",
                    fontWeight: 600,
                    marginTop: "2rem",
                    marginBottom: "0.75rem",
                    lineHeight: 1.4,
                  }}
                >
                  {children}
                </h3>
              ),
              p: ({ children }) => (
                <p
                  style={{
                    marginBottom: "1.5rem",
                    lineHeight: 1.7,
                    color: "rgba(26,26,46,0.8)",
                  }}
                >
                  {children}
                </p>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  style={{
                    color: "#1a1a2e",
                    textDecoration: "underline",
                    textDecorationColor: "rgba(26,26,46,0.3)",
                    textUnderlineOffset: "3px",
                  }}
                  target={href?.startsWith("http") ? "_blank" : undefined}
                  rel={
                    href?.startsWith("http") ? "noopener noreferrer" : undefined
                  }
                >
                  {children}
                </a>
              ),
              blockquote: ({ children }) => (
                <blockquote
                  style={{
                    borderLeft: "4px solid rgba(26,26,46,0.15)",
                    paddingLeft: "1.5rem",
                    margin: "2rem 0",
                    fontStyle: "italic",
                    color: "rgba(26,26,46,0.6)",
                  }}
                >
                  {children}
                </blockquote>
              ),
              code: ({ inline, children }) =>
                inline ? (
                  <code
                    style={{
                      background: "rgba(26,26,46,0.06)",
                      padding: "0.2rem 0.4rem",
                      borderRadius: "0.25rem",
                      fontSize: "0.9em",
                      fontFamily: "Monaco, Consolas, monospace",
                      color: "#1a1a2e",
                    }}
                  >
                    {children}
                  </code>
                ) : (
                  <pre
                    style={{
                      background: "rgba(26,26,46,0.04)",
                      padding: "1.5rem",
                      borderRadius: "0.75rem",
                      overflow: "auto",
                      margin: "2rem 0",
                      border: "1px solid rgba(26,26,46,0.08)",
                    }}
                  >
                    <code
                      style={{
                        fontFamily: "Monaco, Consolas, monospace",
                        fontSize: "0.9em",
                        color: "#1a1a2e",
                      }}
                    >
                      {children}
                    </code>
                  </pre>
                ),
              ul: ({ children }) => (
                <ul
                  style={{
                    paddingLeft: "1.5rem",
                    marginBottom: "1.5rem",
                    color: "rgba(26,26,46,0.8)",
                  }}
                >
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol
                  style={{
                    paddingLeft: "1.5rem",
                    marginBottom: "1.5rem",
                    color: "rgba(26,26,46,0.8)",
                  }}
                >
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li
                  style={{
                    marginBottom: "0.5rem",
                    lineHeight: 1.6,
                  }}
                >
                  {children}
                </li>
              ),
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
      </article>
    </div>
  );
}
